import { useId } from "react";
import { Button, ChoiceControl } from "@carepass/ui";
import "./AgreementGroup.css";

export interface AgreementItem {
  termVersionId: number;
  title: string;
  required: boolean;
  agreed: boolean;
}

export interface AgreementGroupProps {
  items: readonly AgreementItem[];
  allLabel?: string;
  allNote?: string | false;
  presentation?: "default" | "grouped";
  showRequirement?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  onItemChange: (termVersionId: number, agreed: boolean) => void;
  onAllChange: (agreed: boolean) => void;
  onOpenTerm: (termVersionId: number) => void;
}

export function AgreementGroup({
  items,
  allLabel = "전체 동의",
  allNote = "선택 항목 포함",
  presentation = "default",
  showRequirement = true,
  disabled = false,
  readOnly = false,
  error,
  onItemChange,
  onAllChange,
  onOpenTerm,
}: AgreementGroupProps) {
  const errorId = useId();
  const allChecked = items.length > 0 && items.every((item) => item.agreed);
  const partiallyChecked = !allChecked && items.some((item) => item.agreed);

  return (
    <fieldset
      aria-describedby={error ? errorId : undefined}
      className={`partner-agreement-group${presentation === "grouped" ? " partner-agreement-group--grouped" : ""}`}
      disabled={disabled}
    >
      <legend>약관 동의</legend>
      <div className="partner-agreement-group__all">
        <ChoiceControl
          disabled={readOnly}
          aria-label={allNote ? `${allLabel} (${allNote})` : allLabel}
          checked={allChecked}
          indeterminate={partiallyChecked}
          label={(
            <>
              <span className="partner-agreement-group__all-title">{allLabel}</span>
              {allNote && <span className="partner-agreement-group__all-note">{allNote}</span>}
            </>
          )}
          onChange={(event) => onAllChange(event.currentTarget.checked)}
        />
      </div>
      <ul className="partner-agreement-group__items">
        {items.map((item) => (
          <li data-required={item.required || undefined} key={item.termVersionId}>
            <ChoiceControl
              className={presentation === "grouped" ? "partner-agreement-group__checkbox" : undefined}
              aria-label={presentation === "grouped" ? `${item.required ? "필수" : "선택"} ${item.title} 동의` : undefined}
              disabled={readOnly}
              checked={item.agreed}
              label={presentation === "grouped" ? "" : (
                <>
                  {showRequirement && <span className="partner-agreement-group__requirement">
                    {item.required ? "필수" : "선택"}
                  </span>}
                  <span>{item.title}</span>
                </>
              )}
              onChange={(event) => onItemChange(item.termVersionId, event.currentTarget.checked)}
            />
            {presentation === "grouped" ? <button
              type="button"
              className="partner-agreement-group__term"
              aria-label={`${item.title} 내용 보기`}
              disabled={disabled}
              onClick={() => onOpenTerm(item.termVersionId)}
            >
              {showRequirement && <span className="partner-agreement-group__requirement">{item.required ? "필수" : "선택"}</span>}
              <span>{item.title}</span>
            </button> : <Button
              aria-label={`${item.title} 내용 보기`}
              disabled={disabled}
              onClick={() => onOpenTerm(item.termVersionId)}
              size="small"
              variant="quiet"
            >
              보기 <span aria-hidden="true">›</span>
            </Button>}
          </li>
        ))}
      </ul>
      {error && <p className="partner-agreement-group__error" id={errorId} role="alert">{error}</p>}
    </fieldset>
  );
}
